import Data, { treeNode } from 'data'

import { isDev } from '@/common'
import { PassiveSkillDefinition, PassiveSkillWithRange, Range } from '@/data/types'
import { tattooLimits } from '@/logic/tattoos'
import { textRenderer } from '@/tree/text'
import { TooltipLine } from '@/types/common'
import { useAlt } from '@/ui/modifiers'
import classNames from 'classnames'
import React from 'react'
import { FormatLineBr, FormatLines, statLines } from './common'

function RenderedText({ text }: { text: string }) {
  const [loaded, setLoaded] = React.useState(false)
  const ref = React.useRef<HTMLCanvasElement>(null)
  React.useLayoutEffect(() => {
    setLoaded(false)
    let current = true
    textRenderer.load(text).then(() => {
      if (current) setLoaded(true)
    })
    return () => {
      current = false
    }
  }, [text])
  React.useLayoutEffect(() => {
    if (!loaded) return
    const ctx = ref.current!.getContext('2d')!
    const width = textRenderer.measure(ctx, text, 24)
    ref.current!.width = width
    ref.current!.height = 24
    ref.current!.style.width = `${width}px`
    ref.current!.style.height = `24px`
    textRenderer.draw(ctx, text, 0, 24, 24)
  }, [text, loaded])
  return (
    <div className="poe-passiveName poe-rendered">
      <canvas ref={ref} />
    </div>
  )
}

function PassiveTitle({ text }: { text: string }) {
  if (text.match(/<<\w+>>/)) {
    return <RenderedText text={text} />
  } else {
    return <div className="poe-passiveName">{text}</div>
  }
}

export function PassiveSkillTooltip({
  id,
  skill,
  masteryIndex,
  atlas,
  onSelect,
  activeMasteries
}: {
  id?: number
  skill: PassiveSkillWithRange
  masteryIndex?: number
  atlas?: boolean
  onSelect?: (index: number) => void
  activeMasteries?: number[]
}) {
  const altKey = useAlt()

  let type = 'normal'
  if (skill.is_jewel_socket) {
    type = 'jewel'
  } else if (skill.ascendancy) {
    type = 'ascendancy'
  } else if (skill.is_notable) {
    type = 'notable'
  } else if (skill.is_keystone) {
    type = 'keystone'
  } else if (skill.is_just_icon) {
    type = masteryIndex != null || onSelect ? 'masteryActive' : 'mastery'
  }

  const reminderStrings = new Set(skill.reminder)

  let masteries:
    | {
        lines: TooltipLine[]
        reminders?: Set<string>
      }[]
    | undefined
  if (skill.mastery) {
    masteries = skill.mastery.effects.map((stats, index) => {
      const reminders = new Set<string>()
      const lines = statLines('explicitMod', stats, 'passive_skill', undefined, undefined, reminders)
      if (skill.mastery!.text) {
        lines.push(
          ...skill.mastery!.text[index].map((text) => ({
            text,
            type: 'explicitMod'
          }))
        )
      }
      return { lines, reminders }
    })
  }

  const disabledLines: TooltipLine[] = []
  const explicitLines: TooltipLine[] = []
  const auraLines: TooltipLine[] = []
  const extraLines: TooltipLine[] = []

  let baseStats = skill.stats
  if (skill.baseStats) {
    baseStats = {}
    const extraStats: Record<string, number | Range> = {}
    for (const [id, value] of Object.entries(skill.stats)) {
      if (skill.baseStats[id]) {
        baseStats[id] = value
      } else {
        extraStats[id] = value
      }
    }
    extraLines.push(
      ...statLines('explicitMod', extraStats, atlas ? 'atlas' : 'passive_skill', undefined, undefined, reminderStrings)
    )
  }
  explicitLines.push(
    ...statLines('explicitMod', baseStats, atlas ? 'atlas' : 'passive_skill', undefined, undefined, reminderStrings)
  )
  if (skill.aura) {
    auraLines.push(
      ...statLines('explicitMod', skill.aura.stats, 'passive_skill_aura', undefined, undefined, reminderStrings)
    )
  }

  const reminderLines = [...reminderStrings].map((str) => ({
    text: Data.reminder_text[str] ?? str,
    type: 'reminderString'
  }))

  if (skill.tattoo) {
    const tattoo = Data.tattoos[skill.tattoo]
    if (tattoo.limit) {
      extraLines.push({
        text: `Limited to ${tattooLimits[tattoo.limit]}`,
        type: 'explicitMod'
      })
    }
    if (tattoo.requiresAdjacent) {
      extraLines.push({
        text: `Requires at least ${tattoo.requiresAdjacent} adjacent Passive Skills to be allocated`,
        type: 'explicitMod'
      })
      if (skill.disabled) {
        disabledLines.push({
          text: `This Passive Skill is disabled because it requires that at least ${tattoo.requiresAdjacent} adjacent Passive Skills to be allocated`,
          type: 'colourUnmet'
        })
      }
    }
    if (tattoo.maxAdjacent) {
      extraLines.push({
        text: `Requires that no more than ${tattoo.maxAdjacent} adjacent Passive Skill is allocated`,
        type: 'explicitMod'
      })
      if (skill.disabled) {
        disabledLines.push({
          text: `This Passive Skill is disabled because it requires that no more than ${tattoo.maxAdjacent} adjacent Passive Skill is allocated`,
          type: 'colourUnmet'
        })
      }
    }
    reminderLines.push({
      text: `(Tattoo can be removed by using an Orb of Scouring on this Passive Skill)`,
      type: 'reminderString'
    })
  }

  return (
    <div
      className={classNames(`poe-passivePopup poe-${type}Popup`, {
        'poe-AltKey': altKey
      })}>
      <div className="poe-passiveHeader">
        <div className="poe-passiveName">
          <PassiveTitle text={id ? `${skill.name}${isDev ? ` [${id}]` : ''}` : skill.name} />
        </div>
      </div>
      <div className="poe-content">
        {disabledLines.length > 0 && (
          <div className="poe-section">
            <FormatLines lines={disabledLines} />
          </div>
        )}
        <div className="poe-section">
          {explicitLines.length > 0 && <FormatLines lines={explicitLines} />}
          {!!skill.statsText && (
            <FormatLines
              lines={skill.statsText.map((text) => ({
                type: 'explicitMod',
                text
              }))}
            />
          )}
          {auraLines.length > 0 && <FormatLines lines={auraLines} />}
        </div>
        {extraLines.length > 0 && (
          <div className="poe-section">
            <FormatLines lines={extraLines} />
          </div>
        )}
        {!!masteries &&
          masteries.map(({ lines, reminders }, index) => (
            <React.Fragment key={index}>
              <div
                className={classNames('poe-mastery', {
                  'poe-active': index === masteryIndex || activeMasteries?.includes(index),
                  'poe-disabled':
                    (masteryIndex == null && !onSelect) || (index !== masteryIndex && activeMasteries?.includes(index))
                })}
                onClick={onSelect ? () => onSelect(index) : undefined}>
                <div className="poe-checkbox" />
                <div className="poe-stats">
                  <FormatLines lines={lines} />
                </div>
              </div>
              {reminders && (
                <div className="poe-mastery-reminders">
                  <FormatLines
                    lines={[...reminders].map((str) => ({
                      text: Data.reminder_text[str] ?? str,
                      type: 'reminderString'
                    }))}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        {reminderLines.length > 0 && (
          <div className="poe-section poe-reminderSection">
            <FormatLines lines={reminderLines} />
          </div>
        )}
        {!!skill.flavour_text && (
          <div className="poe-section">
            <FormatLineBr type="flavourText" text={skill.flavour_text} />
          </div>
        )}
      </div>
    </div>
  )
}

export function PassiveNodeTooltip({
  version,
  id,
  atlas,
  ...props
}: {
  version?: number
  id: string | number
  masteryIndex?: number
  atlas?: boolean
  onSelect?: (index: number) => void
}) {
  const skill = treeNode(id, version, atlas)
  if (!skill) return <div />

  return <PassiveSkillTooltip skill={skill} atlas={atlas} {...props} />
}

export function TattooTooltip({ id }: { id: string }) {
  const tattoo = Data.tattoos[id]
  if (tattoo.allocatedSkill) {
    return <PassiveSkillTooltip skill={Data.passive_skills[tattoo.allocatedSkill]} />
  }
  if (!tattoo.name) return null
  const skill: PassiveSkillDefinition = {
    name: tattoo.name,
    icon: tattoo.icon ?? 'No icon',
    stats: tattoo.stats,
    tattoo: id
  }
  return <PassiveSkillTooltip skill={skill} />
}
